import React, { useEffect, useState, useCallback } from "react";
import { getFurniBillboard } from "../services/api";

const SLIDE_INTERVAL = 5000;
const CLIENT_TTL_MS = 5 * 60 * 1000;

const BILLBOARD_W = 347;
const BILLBOARD_H = 110;
const TEXT_DARK = "#2e2620";
const TEXT_MUTED = "#4a4038";
const LINE = "rgba(46,38,32,0.35)";
const BORDER = "rgba(46,38,32,0.22)";

let itemsCache = null;
let itemsCachedAt = 0;

function FurniBillboard({ shiftX = 0 }) {
    const [items, setItems] = useState(() =>
        itemsCache && Date.now() - itemsCachedAt < CLIENT_TTL_MS ? itemsCache : []
    );
    const [ready, setReady] = useState(() => items.length > 0);
    const [index, setIndex] = useState(0);
    const [paused, setPaused] = useState(false);
    const [userPaused, setUserPaused] = useState(false);

    useEffect(() => {
        if (itemsCache && Date.now() - itemsCachedAt < CLIENT_TTL_MS) {
            setItems(itemsCache);
            setReady(true);
            return;
        }

        let alive = true;

        (async () => {
            try {
                const data = await getFurniBillboard();
                const list = Array.isArray(data?.items)
                    ? data.items.filter((it) => it && it.name && it.image)
                    : [];
                itemsCache = list;
                itemsCachedAt = Date.now();
                if (!alive) return;
                setItems(list);
                setIndex(0);
                setReady(true);
            } catch {
                if (!alive) return;
                setItems([]);
                setReady(false);
            }
        })();

        return () => {
            alive = false;
        };
    }, []);

    const go = useCallback(
        (dir) => {
            if (items.length < 2) return;
            setIndex((i) => (i + dir + items.length) % items.length);
        },
        [items.length]
    );

    useEffect(() => {
        if (!ready || paused || userPaused || items.length < 2) return;
        const t = setInterval(() => setIndex((i) => (i + 1) % items.length), SLIDE_INTERVAL);
        return () => clearInterval(t);
    }, [ready, paused, userPaused, items.length]);

    const loaded = ready && items.length > 0;
    const current = loaded ? items[index] || items[0] : null;
    const slideKey = loaded ? current.name || index : "loading";

    return (
        <div
            className="hidden lg:block absolute right-0 bottom-0 z-[20] select-none"
            style={{ width: BILLBOARD_W, height: BILLBOARD_H, transform: `translateX(${-shiftX}px)` }}
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
        >
            <div
                className="relative w-full h-full"
                style={{
                    backgroundImage: `url(${process.env.PUBLIC_URL}/img/billboard.png)`,
                    backgroundRepeat: "no-repeat",
                    imageRendering: "pixelated",
                }}
            >
                <div className="absolute" style={{ left: 6, top: 6, right: 6, bottom: 15 }}>
                    <div
                        className="absolute overflow-hidden rounded-[3px] flex items-center justify-center"
                        style={{ left: 2, top: 2, bottom: 1, width: 84, border: `1px solid ${BORDER}` }}
                    >
                        {loaded && (
                            <div key={slideKey} className="billboard-slide flex items-center justify-center">
                                <img
                                    src={current.image}
                                    alt={current.name}
                                    className="block max-w-none"
                                    style={{ imageRendering: "pixelated" }}
                                    draggable={false}
                                />
                            </div>
                        )}
                    </div>

                    <div className="absolute flex flex-col" style={{ left: 94, right: 8, top: 0 }}>
                        <div
                            className="text-center font-['Jersey_10'] text-lg leading-none tracking-[1px] pt-[3px] pb-[2px]"
                            style={{ color: TEXT_DARK, borderBottom: `1px solid ${LINE}`, paddingRight: 54 }}
                        >
                            Latest Furni!
                        </div>

                        {loaded ? (
                            <div key={`t-${slideKey}`} className="billboard-slide flex flex-col">
                                <div
                                    className="font-volterBold truncate pl-[2px] pt-[6px] pb-[2px]"
                                    style={{ fontSize: 9, lineHeight: "9px", color: TEXT_DARK, borderBottom: `1px solid ${LINE}` }}
                                >
                                    {current.name}
                                </div>
                                <div
                                    className="font-volter line-clamp-2 pl-[2px] pt-[2px]"
                                    style={{ fontSize: 9, lineHeight: "9px", color: TEXT_MUTED }}
                                >
                                    {current.motto}
                                </div>
                            </div>
                        ) : (
                            <div
                                className="font-volter pl-[2px] pt-[8px]"
                                style={{ fontSize: 9, lineHeight: "11px", color: TEXT_MUTED }}
                            >
                                Connecting to Furni Index...
                            </div>
                        )}
                    </div>

                    <div className="absolute flex items-center gap-1" style={{ top: 3, right: 8, visibility: loaded ? "visible" : "hidden" }}>
                        <button
                            type="button"
                            aria-label="Previous furni"
                            onClick={() => go(-1)}
                            className="rounded-full bg-[#6f6a62] hover:bg-[#837c72] text-white flex items-center justify-center transition-colors"
                            style={{ width: 16, height: 16 }}
                        >
                            <svg width="10" height="10" viewBox="0 0 24 24" aria-hidden="true">
                                <path d="M15 4l-8 8 8 8" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </button>
                        <button
                            type="button"
                            aria-label={userPaused ? "Resume slideshow" : "Pause slideshow"}
                            onClick={() => setUserPaused((p) => !p)}
                            className="rounded-full bg-[#6f6a62] hover:bg-[#837c72] text-white flex items-center justify-center transition-colors"
                            style={{ width: 16, height: 16 }}
                        >
                            {userPaused ? (
                                <svg width="9" height="9" viewBox="0 0 24 24" aria-hidden="true">
                                    <path d="M7 4l13 8-13 8z" fill="currentColor" />
                                </svg>
                            ) : (
                                <svg width="9" height="9" viewBox="0 0 24 24" aria-hidden="true">
                                    <rect x="6" y="4" width="4.5" height="16" rx="1.2" fill="currentColor" />
                                    <rect x="13.5" y="4" width="4.5" height="16" rx="1.2" fill="currentColor" />
                                </svg>
                            )}
                        </button>
                        <button
                            type="button"
                            aria-label="Next furni"
                            onClick={() => go(1)}
                            className="rounded-full bg-[#6f6a62] hover:bg-[#837c72] text-white flex items-center justify-center transition-colors"
                            style={{ width: 16, height: 16 }}
                        >
                            <svg width="10" height="10" viewBox="0 0 24 24" aria-hidden="true">
                                <path d="M9 4l8 8-8 8" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </button>
                    </div>

                    <div className="absolute leading-none" style={{ bottom: 0, right: 6, color: TEXT_MUTED }}>
                        <span style={{ fontSize: 10 }}>Powered by </span>
                        <a
                            href="https://furniindex.com/"
                            target="_blank"
                            rel="noreferrer"
                            className="hover:underline"
                            style={{ color: TEXT_MUTED }}
                        >
                            <span className="font-furniIndex font-normal tracking-normal" style={{ fontSize: 12 }}>
                                Furni Index
                            </span>
                            <span className="opacity-90" style={{ fontSize: 10 }}>
                                {" "}
                                ⧉
                            </span>
                        </a>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default FurniBillboard;
